import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

/**
 * Shared state for the GDPR action controls (page navigation, Breach, Blank, Clear Labels).
 *
 * In V2, PdfViewer still owns the action hooks (they depend on its DOM-bound layout/virtualization
 * refs and grid navigation). Rather than lifting all of that state up, PdfViewer publishes the
 * outwardly-relevant slice via this context, and the Sidebar consumes it to render the controls.
 */
/** Per the radio-selector view: detected-only labels map to 'none'. Only confirmed labels count. */
export type CurrentPageLabel = 'breach' | 'blank' | 'none';

export interface GdprControlsState {
  currentPage: number;
  totalPages: number;
  // Source-document numbering, used by the sidebar page switcher (mirrors the per-page
  // label rendered under each PDF). May not equal currentPage when pages have been
  // re-ordered or removed from the working set.
  currentOriginalPageNumber: number;
  maxOriginalPageNumber: number;
  isLoading: boolean;
  hasLabeledPages: boolean;
  labeledPageCount: number; // total pages with any label (detected or confirmed) — same set Prev/Next nav cycles through
  confirmedLabeledPageCount: number; // pages the user has explicitly confirmed (Breach or Blank). Used as the review-progress metric.
  currentLabeledPosition: number | null; // 1-based position of the focused page within the labeled set, or null if the focused page has no label
  currentPageLabel: CurrentPageLabel;
  onBreachClick: () => void;
  onBlankClick: () => void;
  onClearLabelsClick: () => void;
  onGoToPage: (pageIndex: number) => void;
  // Navigates by source-document page number (i.e. Page.originalPageNumber). Looks up
  // the matching page in the working set and scrolls to it; no-op if not found.
  onGoToOriginalPageNumber: (originalPageNumber: number) => void;
  onPrevLabeledPage: () => void;
  onNextLabeledPage: () => void;
}

const defaultState: GdprControlsState = {
  currentPage: 1,
  totalPages: 0,
  currentOriginalPageNumber: 1,
  maxOriginalPageNumber: 0,
  isLoading: false,
  hasLabeledPages: false,
  labeledPageCount: 0,
  confirmedLabeledPageCount: 0,
  currentLabeledPosition: null,
  currentPageLabel: 'none',
  onBreachClick: () => {},
  onBlankClick: () => {},
  onClearLabelsClick: () => {},
  onGoToPage: () => {},
  onGoToOriginalPageNumber: () => {},
  onPrevLabeledPage: () => {},
  onNextLabeledPage: () => {},
};

interface ContextValue {
  state: GdprControlsState;
  setState: (partial: Partial<GdprControlsState>) => void;
}

const GdprControlsContext = createContext<ContextValue>({
  state: defaultState,
  setState: () => {},
});

export const GdprControlsProvider = ({ children }: { children: ReactNode }) => {
  const [state, setStateInternal] = useState<GdprControlsState>(defaultState);
  const setState = useCallback((partial: Partial<GdprControlsState>) => {
    setStateInternal((prev) => ({ ...prev, ...partial }));
  }, []);
  return <GdprControlsContext.Provider value={{ state, setState }}>{children}</GdprControlsContext.Provider>;
};

export const useGdprControlsState = () => useContext(GdprControlsContext).state;
export const useGdprControlsSetter = () => useContext(GdprControlsContext).setState;
